import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const ISSUE_TYPES = [
  "Cannot access account", "Wrong mobile number", "Mistaken identity", "Complaint challenge",
  "Privacy concern", "Evidence issue", "Harassment", "False complaint", "Settlement problem",
  "Account deletion", "Technical issue", "Other",
];

export default function Support() {
  const { user, openAuth } = useAuth();
  const [f, setF] = useState({ complaint_id: "", issue_type: "Technical issue", description: "", preferred_channel: "in_app" });
  const [tickets, setTickets] = useState([]);

  const load = () => { if (user) api.get("/support/tickets/mine").then((r) => setTickets(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [user]);

  const submit = async () => {
    if (!user) return openAuth("login");
    if (!f.description) return toast.error("Please describe your issue");
    try {
      await api.post("/support/tickets", f);
      toast.success("Support ticket created");
      setF({ complaint_id: "", issue_type: "Technical issue", description: "", preferred_channel: "in_app" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create ticket");
    }
  };

  return (
    <div className="ca-container py-12 grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2">
          <LifeBuoy className="h-8 w-8 text-teal" /> Support
        </h1>
        <p className="text-slate-600 mt-2">Raise a support ticket for account, privacy, evidence, or complaint-challenge matters.</p>
        <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
          <div><Label>Complaint ID (optional)</Label><Input data-testid="ticket-complaint-id" value={f.complaint_id} onChange={(e) => setF({ ...f, complaint_id: e.target.value })} /></div>
          <div>
            <Label>Issue type</Label>
            <Select value={f.issue_type} onValueChange={(v) => setF({ ...f, issue_type: v })}>
              <SelectTrigger data-testid="ticket-issue-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea rows={4} data-testid="ticket-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div>
            <Label>Preferred response channel</Label>
            <Select value={f.preferred_channel} onValueChange={(v) => setF({ ...f, preferred_channel: v })}>
              <SelectTrigger data-testid="ticket-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_app">In-app</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="bg-teal-brand hover:bg-teal-700 text-white" onClick={submit} data-testid="ticket-submit">Submit ticket</Button>
        </div>
      </div>
      <aside>
        <h3 className="font-display font-bold text-slate-900 mb-3">My tickets</h3>
        <div className="space-y-2">
          {tickets.length === 0 && <p className="text-sm text-slate-500">No tickets yet.</p>}
          {tickets.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <p className="text-sm font-semibold text-slate-800">{t.ticket_code}</p>
              <p className="text-xs text-slate-500">{t.issue_type} · {t.status}</p>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
